import { saveAs } from 'file-saver';
import {
  Document,
  Paragraph,
  TextRun,
  Packer,
  AlignmentType,
} from 'docx';

import moment from 'moment';
import { MONTH_GENITIVE, MONTH_GENITIVE_NEW } from '../../../../../assets/const';

// ─── Константы ────────────────────────────────────────────────────────────────

const COMISSION_HEAD = [
  { position: 'Председатель Аттестационной комиссии Министерства обороны Российской Федерации', value: 'chairman' },
  { position: 'заместитель председателя Аттестационной комиссии Министерства обороны Российской Федерации', value: 'deputy' },
  { position: 'секретарь Аттестационной комиссии Министерства обороны Российской Федерации', value: 'secretary' },
];

// ─── Утилиты ──────────────────────────────────────────────────────────────────

const getInitials = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return fullName;
  return `${parts[0] || ''} ${parts[1]?.charAt(0) || ''}.${parts[2]?.charAt(0) || ''}.`;
};

const formatDate = (date) => moment(date).format(`DD ${MONTH_GENITIVE[moment(date).month()]} YYYY`);
const formatDateNew = (date) => moment(date).format(`${MONTH_GENITIVE_NEW[moment(date).month()]} YYYY`);

// ─── Функции форматирования ───────────────────────────────────────────────────

const createTextParagraph = (text, options = {}) => {
  const {
    bold = false,
    italic = false,
    size,
    alignment = AlignmentType.LEFT,
    indentLeft = 0,
    indentFirstLine = 0,
    spacingAfter = 300,
    font = 'Times New Roman',
    rightAligned = false,
  } = options;

  const safeText = text || "";

  const textRunOptions = {
    text: safeText,
    bold,
    italic,
    font: { name: font },
  };

  if (size != null && !isNaN(size) && size > 0 && size <= 100) {
    textRunOptions.size = size * 2;
  }

  const children = [new TextRun(textRunOptions)];

  // Для выравнивания по правому краю — добавляем табуляцию
  if (rightAligned) {
    children.unshift(
      new TextRun({
        text: "\t",
        font: { name: font },
      })
    );
  }

  const indent = {};
  if (indentLeft > 0) indent.left = indentLeft;
  if (indentFirstLine !== 0) indent.firstLine = indentFirstLine;

  return new Paragraph({
    children,
    alignment: rightAligned ? AlignmentType.RIGHT : alignment,
    indent: Object.keys(indent).length > 0 ? indent : undefined,
    spacing: { after: spacingAfter },
    tabStops: rightAligned ? [{ position: 9000 }] : undefined,
  });
};

const createJustifiedParagraph = (text, bold = false) => {
  return createTextParagraph(text, {
    bold,
    alignment: AlignmentType.JUSTIFIED,
    indentFirstLine: 720,
  });
};

const createCenteredParagraph = (text, bold = true, size = 12) => {
  return createTextParagraph(text, {
    bold,
    size,
    alignment: AlignmentType.CENTER,
  });
};

const createPageBreak = () =>
  new Paragraph({
    children: [
      new TextRun({
        text: "\u00A0",
        font: { name: "Times New Roman" },
      }),
    ],
    pageBreakBefore: true,
    spacing: { before: 0, after: 0 },
  });

// ─── Основная функция ─────────────────────────────────────────────────────────

const getProtocolGukDocx = async ({ data, comission, userInfo }) => {
  const { last_name, first_name, patronymic } = userInfo;
  const createrName = `${last_name || ''} ${first_name || ''} ${patronymic || ''}`;
  const dateTo = comission?.date_to;
  const formattedDate = formatDate(dateTo);
  const formattedDateNew = formatDateNew(dateTo);

  // Создаём объект комиссии
  const comissionObject = (data.experts || []).reduce((acc, expert) => ({ ...acc, [expert.place]: expert }), {});
  const comissionMembers = (data.experts || []).filter(i => i.place === 'member');

  // Формируем содержимое документа
  const content = [];

  // === ЗАГОЛОВОК ПРОТОКОЛА ===

  content.push(
    createCenteredParagraph('Протокол № ___', true, 13),
    createJustifiedParagraph('заседания Аттестационной комиссии Министерства обороны Российской Федерации по аттестации педагогических и руководящих работников организаций, осуществляющих образовательную деятельность и находящихся в ведении Министерства обороны Российской Федерации,'),
    createCenteredParagraph(`от ${formattedDate} года`, true, 13)
  );

  // === СОСТАВ КОМИССИИ ===

  content.push(
    createTextParagraph('Присутствовали:', true, { size: 12, spacingAfter: 400 })
  );

  COMISSION_HEAD.forEach((item) => {
    const expert = comissionObject[item.value] || {};
    const { LastName, FirstName, Patronymic, position } = expert;
    const fio = getInitials(`${LastName || ''} ${FirstName || ''} ${Patronymic || ''}`);
    const text = `${item.position} ${fio} - ${position}`;
    content.push(createTextParagraph(text, { indentLeft: 720 }));
  });

  content.push(
    createTextParagraph('Члены комиссии:', true, { size: 12, spacingAfter: 400 })
  );

  comissionMembers.forEach((expert) => {
    const { LastName, FirstName, Patronymic, position } = expert;
    const fio = getInitials(`${LastName || ''} ${FirstName || ''} ${Patronymic || ''}`);
    const text = `${fio} - ${position}`;
    content.push(createTextParagraph(text, { indentLeft: 720 }));
  });

  // === ПОВЕСТКА ДНЯ ===

  content.push(
    createCenteredParagraph('Повестка дня:', true, 12),
    createJustifiedParagraph(`1. Рассмотрение аттестационных материалов педагогических и руководящих работников с целью установления первой квалификационной категории в ${formattedDateNew} года.`),
    createJustifiedParagraph(`2. Рассмотрение аттестационных материалов педагогических и руководящих работников с целью установления высшей квалификационной категории в ${formattedDateNew} года.`)
  );

  // === ХОД ЗАСЕДАНИЯ ===

  content.push(
    createCenteredParagraph('Ход заседания:', true, 12),
    createJustifiedParagraph('1. Заслушали секретаря Аттестационной комиссии Министерства обороны Российской Федерации Г.Г.Маркевича по вопросу проведения аттестации педагогических и руководящих работников организаций, осуществляющих образовательную деятельность и находящихся в ведении Министерства обороны Российской Федерации, с целью установления'),
    createTextParagraph('первой квалификационной категории.', true, { indentLeft: 720 }),
    createJustifiedParagraph('По результатам экспертизы представленных аттестационных материалов считать уровень квалификации следующих педагогических и руководящих работников соответствующим требованиям, предъявляемым к'),
    createTextParagraph('первой квалификационной категории:', true, { indentLeft: 720 })
  );

  // Списки по школам — первая категория
  if (data.reasons_statistic_first_category) {
    Object.keys(data.reasons_statistic_first_category).forEach((school) => {
      content.push(createCenteredParagraph(school, true, 12));
      (data.reasons_statistic_first_category[school] || []).forEach((item, ind) => {
        const fio = `${item.lastName || ''} ${item.firstName || ''} ${item.patronymic || ''}, ${item.position || ''}`;
        content.push(createTextParagraph(`${ind + 1}. ${fio}`, { indentLeft: 720 }));
      });
    });
  }

  content.push(
    createCenteredParagraph('Решение комиссии:', true, 12),
    createJustifiedParagraph('Установить первую квалификационную категорию вышеперечисленным педагогическим работникам организаций, осуществляющих образовательную деятельность и находящихся в ведении Министерства обороны Российской Федерации, по занимаемым должностям.'),
    createTextParagraph('Результаты голосования:', true),
    createTextParagraph('«за» – 9'),
    createTextParagraph('«против» – 0')
  );

  // === ВЫСШАЯ КАТЕГОРИЯ ===

  content.push(
    createJustifiedParagraph('2. Заслушали секретаря Аттестационной комиссии Министерства обороны Российской Федерации Г.Г.Маркевича по вопросу проведения аттестации педагогических и руководящих работников организаций, осуществляющих образовательную деятельность и находящихся в ведении Министерства обороны Российской Федерации, с целью установления'),
    createTextParagraph('высшей квалификационной категории.', true, { indentLeft: 720 }),
    createJustifiedParagraph('По результатам экспертизы представленных аттестационных материалов считать уровень квалификации следующих педагогических и руководящих работников соответствующим требованиям, предъявляемым к'),
    createTextParagraph('высшей квалификационной категории:', true, { indentLeft: 720 })
  );

  // Списки по школам — высшая категория
  if (data.reasons_statistic_highest_category) {
    Object.keys(data.reasons_statistic_highest_category).forEach((school) => {
      content.push(createCenteredParagraph(school, true, 12));
      (data.reasons_statistic_highest_category[school] || []).forEach((item, ind) => {
        const fio = `${item.lastName || ''} ${item.firstName || ''} ${item.patronymic || ''}, ${item.position || ''}`;
        content.push(createTextParagraph(`${ind + 1}. ${fio}`, { indentLeft: 720 }));
      });
    });
  }

  content.push(
    createCenteredParagraph('Решение экспертной группы:', true, 12),
    createJustifiedParagraph('Установить высшую квалификационную категорию вышеперечисленным педагогическим работникам организаций, осуществляющих образовательную деятельность и находящихся в ведении Министерства обороны Российской Федерации, по занимаемым должностям.'),
    createTextParagraph('Результаты голосования:', true, { spacingAfter: 600 }),
    createTextParagraph('«за» – 9'),
    createTextParagraph('«против» – 0')
  );

  // === ПОДПИСИ ===

  content.push(
    createTextParagraph('Руководитель экспертной группы:', { spacingAfter: 400 }),
    createTextParagraph('Н.А. Близнова', { rightAligned: true, spacingAfter: 600 }),

    createTextParagraph('Члены Аттестационной экспертной', { spacingAfter: 100 }),
    createTextParagraph('Министерства обороны Российской Федерации', { spacingAfter: 400 })
  );

  comissionMembers.forEach((item) => {
    const fio = getInitials(`${item.LastName || ''} ${item.FirstName || ''} ${item.Patronymic || ''}`);
    content.push(createTextParagraph(fio, { rightAligned: true, spacingAfter: 200 }));
  });

  // === ПОДВАЛ: ИНФО О ДОКУМЕНТЕ (БЕЗ КАРТИНОК) ===

  content.push(
    createPageBreak(),
    createTextParagraph(`Сформировано: ${createrName}`, { size: 8, alignment: AlignmentType.LEFT, spacingAfter: 50 }),
    createTextParagraph(`Дата: ${moment(dateTo).format('DD.MM.YYYY')}`, { size: 8, alignment: AlignmentType.LEFT, spacingAfter: 50 }),
    createTextParagraph("Документ сформирован в системе управления обучением", { size: 8, italic: true, alignment: AlignmentType.LEFT, spacingAfter: 100 })
  );

  // Создаём документ
  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: "Times New Roman",
            size: 24, // 12pt
          },
        },
      },
    },
    sections: [
      {
        children: content,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  // saveAs(blob, 'Протокол_аттестационной_комиссии.docx');
  return blob;
};

export default getProtocolGukDocx;